import type {
  CalculatedMinMaxBoundsPayload,
  LegendItemVisibility,
  ChartDeletePayload,
  WebChart,
  WebChartDataItem,
  WebChartHistogramDataItem,
  WebChartScatterPlotDataItem,
  WebChartGenericDataItem,
  WebMapWebChart,
  WebChartLegendPositions,
  WebChartBarChartSeries,
  WebChartLineChartSeries,
  WebChartPieChartSeries,
  WebChartBoxPlotSeries,
  WebChartHistogramSeries,
  WebChartScatterPlotSeries,
  SupportedLayer,
} from "@arcgis/charts-spec";
import { WebChartTypes, RESTStatisticType } from "@arcgis/charts-spec";
import type {
  ValidationStatus,
  ConfigWarningObject,
  SelectionData,
  SeriesSymbolsMap,
  PieSlicesSymbols,
  SeriesSymbols,
  ActionBarHideActionsProps,
  ChartTypes,
  StringIndexable,
  ChartConfig,
} from "@arcgis/charts-shared-utils";
import {
  isChartRotated,
  ConfigWarningKeywords,
  fetchComponentLocaleStrings,
  canShowLegend,
  ActionModes,
  validateWebChart,
  getDataMinMaxFromSeries,
  isPartialConfig,
  convertChartSpec,
  SelectionSource,
  getChartOrSeriesType,
  addILayer,
  ChartComponentEvents,
  DefaultChartActions,
  getChartSubType,
  ChartSubType,
  isChartType,
  ActionToggle,
  ActionState,
  LayerFilterChangePolicy,
  getChartIcon,
  getMainLayer,
} from "@arcgis/charts-shared-utils";
import type {
  DefaultActionSelectPayload,
  HTMLArcgisChartsChartElement,
  HTMLArcgisChartsZoomableChartElement,
} from "@arcgis/charts-components/dist/types";
import type MapView from "@arcgis/core/views/MapView";
import type SceneView from "@arcgis/core/views/SceneView";
import type Sublayer from "@arcgis/core/layers/support/Sublayer";
import type LayerView from "@arcgis/core/views/layers/LayerView";
import type FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import type { EventEmitter } from "@stencil/core";
import { Component, Prop, Watch, Element, Event, State, getAssetPath, Method } from "@stencil/core";
import type { PieChartDataModes, ChartModel, ModelConfigChangePayload } from "@arcgis/charts-model";
import {
  BarChartModel,
  HistogramModel,
  LineChartModel,
  PieChartModel,
  ScatterPlotModel,
  BoxPlotModel,
  isModelWithRotation,
  isBarChartModel,
  isLineChartModel,
  isBoxPlotModel,
  isModelWithXYAxes,
  isModelWithLegend,
  isPieChartModel,
} from "@arcgis/charts-model";
import { importCoreReactiveUtils } from "@arcgis/core-adapter";
import { cloneDeep, debounce, isEqual } from "lodash-es";
import { HeightScaleDirections } from "../../interfaces";
import { HTMLClasses } from "./resource";
import { addEventListenersFromMap, DebounceTimers, hasSelectionChanged } from "../../utils/app-utils";

@Component({
  tag: "arcgis-charts-app-container",
  styleUrl: "arcgis-charts-app-container.scss",
  shadow: true,
  assetsDirs: ["assets"],
})
export class ArcgisChartsAppContainer {
  /**
   * @category Public
   */
  @Element() hostElement: HTMLArcgisChartsAppContainerElement;

  /**
   *  Popover placement for placing the calcite popopver element used by chart authoring component
   *  @category Public
   */
  @Prop() public popoverPlacement?: HTMLCalcitePopoverElement["placement"];

  /**
   *  A reference to the DOM node inside which ArcGIS chart authoring component is created
   * @category Public
   */
  @Prop({ mutable: true }) public configReferenceElement?: HTMLElement;

  /**
   *  A reference to the DOM node inside which ArcGIS chart component is created
   *  @category Public
   */
  @Prop({ mutable: true }) public chartReferenceElement?: HTMLElement;

  /**
   *  A reference to the DOM node inside which action bar component is created
   *  @category Public
   */
  @Prop() public actionsReferenceElement?: HTMLElement;

  /**
   * Tab manager element to be used for rendering multiple charts in different tabs.
   * @category Public
   */
  @Prop() public tabManagerElement?: HTMLElement | undefined;

  /**
   *  Heading title for the config create chart Element
   * @category Public
   */
  @Prop() public configHeaderTitle?: string;

  /**
   *  Heading title for the chart Element
   * @category Public
   */
  @Prop() public chartHeaderTitle?: string;

  /**
   * The current instance of the MapView or SceneView
   * @category Public
   */
  @Prop() public view: MapView | SceneView;

  /**
   * The instance of the a feature layer or sublayer to create and edit the charts.
   * @category Public
   */
  @Prop() public layer: Sublayer | SupportedLayer;

  /**
   * A boolean to disable the toggling feature of legend items to show or hide slices for Pie chart.
   * Default value is true.
   * @category Public
   */
  @Prop() public pieChartDisableTogglingLegendItems = true;

  /**
   * A boolean to disable the toggling feature of legend items to show or hide series for multi-series Bar chart.
   * Default value is false.
   * @category Public
   */
  @Prop() public barChartDisableTogglingLegendItems = false;

  /**
   * A boolean to disable the toggling feature of legend items to show or hide series for multi-series Box plot.
   * Default value is false.
   * @category Public
   */
  @Prop() public boxPlotDisableTogglingLegendItems = false;

  /**
   * A boolean to disable the toggling feature of legend items to show or hide series for multi-series Line chart.
   * Default value is false.
   * @category Public
   */
  @Prop() public lineChartDisableTogglingLegendItems = false;

  /**
   * A boolean to disable the toggling feature of legend items to show or hide the overlays for Histogram.
   * Default value is false.
   * @category Public
   */
  @Prop() public histogramDisableTogglingLegendItems = false;

  /**
   * A boolean to disable the toggling feature of legend items to show or hide the trend line for Scatter-plot.
   * Default value is false.
   * @category Public
   */
  @Prop() public scatterPlotDisableTogglingLegendItems = false;

  /**
   * Whether the chart panel is closable (should have close action in menu)
   * @category Public
   */
  @Prop() public chartPanelClosable = true;

  /**
   * Whether the chart panel is scalable (should have resize action in menu)
   * @category Public
   */
  @Prop() public chartPanelScalable = false;

  /**
   * Whether the app should generate its own element references inside shadow DOM
   * @category Public
   */
  @Prop() public generateReferences = false;

  /**
   * Array of chart configs
   * @category Public
   */
  @Prop({ mutable: true, reflect: true }) public configList: WebMapWebChart[];

  /**
   *  Chart ID for the selected chart.
   *  @category Private
   */
  @Prop({ mutable: true, reflect: true }) selectedChartId?: string | undefined;

  /**
   * the open state of chart authoring component
   * @category Private
   */
  @Prop({ mutable: true, reflect: true }) openConfig: boolean;

  /**
   * usePopupTemplateFieldsInfo can be set to true to read the fields from layer.popupTemplate
   * @category Public
   * @default false
   */
  @Prop() usePopupTemplateFieldsInfo?: boolean;

  /**
   * Whether up scale action is disabled
   * @category Public
   */
  @Prop({ reflect: true }) public disableUpScaleAction = false;

  /**
   * Whether down scale action is disabled
   * @category Public
   */
  @Prop({ reflect: true }) public disableDownScaleAction = false;

  /**
   * panel number to keep track of the flow movement.
   * @category Public
   */
  @Prop({ mutable: true, reflect: true }) panelNumber?: number;

  /**
   * Whether the chart action bar is expanded
   * @category Public
   */
  @Prop({ reflect: true }) public actionBarExpanded = false;

  /**
   * Whether the app container should reload chart component automatically when chart ID changes
   * @category Public
   */
  @Prop({ reflect: true }) public autoReloadNewChartIds = true;

  /**
   * Object containing information about the selection to be or currently applied on the chart
   * @category Public
   */
  @Prop({ reflect: true, mutable: true }) public selectionData: SelectionData;

  /**
   * The data used by the chart
   * @category Public
   */
  @Prop({ reflect: true, mutable: true })
  public chartData?: WebChartHistogramDataItem;

  /**
   * R2 value from trendline Used only by scatter plot
   * @category Public
   */
  @Prop({ reflect: true, mutable: true }) public r2?: number;

  /**
   * Whether the edit chart action is disabled
   * @category Public
   */
  @Prop({ reflect: true }) public disableEditAction = false;

  /**
   * Holds properties that determine whether to hide certain action or not in the action bar
   * @category Public
   */
  @Prop({ reflect: true, mutable: true })
  public actionBarHideActionsProps: ActionBarHideActionsProps;

  /**
   * Enables the use of chart components in their beta version.
   * @default false
   * @category Public
   */
  @Prop({ reflect: true, mutable: false })
  public enableChartBetaComponents = false;

  /**
   * The ID string to be used for the chart tab.
   * @category Private
   */
  private readonly chartTabId = "chart-tab-id";

  /**
   * @category Private
   */
  private strings: typeof import("./assets/arcgis-charts-app-container/t9n/arcgis-charts-app-container.t9n.en.json");

  /**
   * The chart model for the chart currently selected.
   * @category Private
   */
  private model: ChartModel | undefined;

  /**
   * @category Private
   */
  private configElement: HTMLArcgisChartsConfigCreateFlowElement | undefined;

  /**
   * @category Private
   */
  private chartElement: HTMLArcgisChartsChartElement | undefined;

  /**
   * @category Private
   */
  private actionBarElement: HTMLArcgisChartsActionBarElement | undefined;

  /**
   * @category Private
   */
  private chartAuthoringActionElement: HTMLCalciteActionElement | undefined;

  /**
   * @category Private
   */
  private chartPanel: HTMLCalcitePanelElement | undefined;

  /**
   * The tab title element for the chart currently selected.
   * @category Private
   */
  private tabTitleElement: HTMLCalciteTabTitleElement | undefined;

  /**
   * @category Private
   */
  private highlightSelect: IHandle;

  /**
   * Map from chart ID to whether the user has customized the time interval (only used for Line Charts).
   * @category Private
   */
  private isTimeIntervalCustomized = new Map<string, boolean>();

  /**
   * Whether a data update is needed on the chart
   * @category Private
   */
  private dataUpdateNeeded = false;

  /**
   * Listener on the layer's filter
   * @category Private
   */
  private layerFilterListener: IHandle | undefined;

  /**
   * Listener on the view's time zone
   * @category Private
   */
  private viewTimeZoneListener: IHandle | undefined;

  /**
   * The feature layer form converted from the `layer` prop.
   * @category Public
   */
  @State() public featureLayer: SupportedLayer;

  // TODO: Replace `modelLoading` with usage of the model's build-in `setupFinished` promise.
  /**
   * Whether the model is currently loading.
   * Used to mark that we are waiting for a new model to finish being created before we try to use it for anything.
   * @category Private
   */
  @State() private modelLoading = true;

  /**
   * Verify if the config opened for configuring new chart
   * @category Private
   */
  @State() private isCreate: boolean;

  /**
   * Called every time configReferenceElement is updated for the config element.
   * @param newElement The new reference html element
   * @param oldElement The old reference html element
   * @category Watch handler
   */
  @Watch("configReferenceElement")
  configReferenceChange(newElement?: HTMLElement, oldElement?: HTMLElement): void {
    oldElement?.remove();
    if (
      newElement !== undefined &&
      this.configReferenceElement?.contains(this.configElement as HTMLElement) === false
    ) {
      this.createConfigElement();
    } else if (newElement === undefined && this.generateReferences) {
      this.createConfigReferenceElement();
    }
  }

  /**
   * Verify if the config opened for configuring new chart
   * if opened, then chart config to empty initially.
   * @param newValue
   */
  @Watch("isCreate")
  isCreateChange(newValue: boolean): void {
    if (newValue) {
      if (this.chartElement !== undefined) {
        this.chartElement.config = undefined;
      }
    }
  }

  /**
   * Called every time chartReferenceElement is updated
   * @param newElement The new reference html element
   * @param oldElement The old reference html element
   * @category Watch handler
   */
  @Watch("chartReferenceElement")
  chartReferenceChange(newElement?: HTMLElement, oldElement?: HTMLElement): void {
    oldElement?.remove();
    if (newElement !== undefined && this.chartReferenceElement?.contains(this.chartElement as HTMLElement) === false) {
      this.createChartElement();
    } else if (newElement === undefined && this.generateReferences) {
      this.createChartReferenceElement();
    }
  }

  /**
   * Called every time actionsReferenceElement is updated
   * @param newElement The new reference html element
   * @param oldElement The old reference html element
   * @category Watch handler
   */
  @Watch("actionsReferenceElement")
  actionsReferenceChange(newElement?: HTMLElement, oldElement?: HTMLElement): void {
    oldElement?.remove();
    if (this.actionBarElement !== undefined) {
      if (newElement !== undefined) {
        this.createActionBarElement(true);
      } else {
        this.createActionBarElement(false);
      }
    }
  }

  /**
   * Called every time the tabManagerElement is updated
   * @category Watch handler
   */
  @Watch("tabManagerElement")
  tabManagerChange(newElement?: HTMLElement, oldElement?: HTMLElement): void {
    // Add event listener to tab manager element for when the entire panel is closed
    oldElement?.removeEventListener("arcgisTabsPanelClose", this.handleChartClose);
    newElement?.addEventListener("arcgisTabsPanelClose", this.handleChartClose);
  }

  /**
   * Called every time the disableTogglingLegendItems is changed, for all chart types.
   * @category Watch handler
   */
  @Watch("pieChartDisableTogglingLegendItems")
  @Watch("barChartDisableTogglingLegendItems")
  @Watch("boxPlotDisableTogglingLegendItems")
  @Watch("lineChartDisableTogglingLegendItems")
  @Watch("histogramDisableTogglingLegendItems")
  @Watch("scatterPlotDisableTogglingLegendItems")
  legendItemsToggleableChange(): void {
    if (this.chartElement !== undefined) {
      let disableTogglingLegendItems: boolean | undefined = false;
      switch (this.model?.getChartType()) {
        case WebChartTypes.PieSeries:
          disableTogglingLegendItems = this.pieChartDisableTogglingLegendItems;
          break;
        case WebChartTypes.BarSeries:
          disableTogglingLegendItems = this.barChartDisableTogglingLegendItems;
          break;
        case WebChartTypes.BoxPlotSeries:
          disableTogglingLegendItems = this.boxPlotDisableTogglingLegendItems;
          break;
        case WebChartTypes.LineSeries:
          disableTogglingLegendItems = this.lineChartDisableTogglingLegendItems;
          break;
        case WebChartTypes.HistogramSeries:
          disableTogglingLegendItems = this.histogramDisableTogglingLegendItems;
          break;
        case WebChartTypes.ScatterSeries:
          disableTogglingLegendItems = this.scatterPlotDisableTogglingLegendItems;
          break;
        default:
          break;
      }
      this.chartElement.disableTogglingLegendItems = disableTogglingLegendItems;
    }
  }

  /**
   * Called every time selectedChartId is updated
   * @category Watch handler
   */
  @Watch("selectedChartId")
  async selectedChartIdChange(_newValue?: string, oldValue?: string): Promise<void> {
    if (this.configElement !== undefined) {
      this.configElement.selectedChartId = this.selectedChartId;
    }

    // remove the old selected config from the chart config list if the config is partial
    const oldConfig = this.configList.find((eachConfig) => eachConfig.id === oldValue);
    if (oldConfig !== undefined && isPartialConfig(oldConfig)) {
      // remove the config from the configList if the config is partial
      this.configList = this.configList.filter((config) => config.id !== oldValue);
      this.arcgisChartsAppChartsListChange.emit({
        chartId: oldValue,
        isDelete: true,
      });
    }

    if (
      this.selectedChartId !== null &&
      this.selectedChartId !== undefined &&
      this.model?.getConfig().id !== this.selectedChartId
    ) {
      this.modelLoading = true;
      const config = this.configList.find((eachConfig) => eachConfig.id === this.selectedChartId);
      const chartType = getChartOrSeriesType(config?.series);
      await this.createModel({ chartType, config });
    } else if (this.selectedChartId === null || this.selectedChartId === undefined) {
      this.closeChart();
    }
  }

  /**
   * Called every time configList is updated
   * @category Watch handler
   */
  @Watch("configList")
  configListChange(): void {
    // check for any duplicate ids in the configList and replace them with new ids
    const configIds = new Set<string | undefined>();
    this.configList?.forEach((config) => {
      if (configIds.has(config.id) || config.id === undefined || config.id === "") {
        config.id = `Chart_${Date.now()}`;
      }
      configIds.add(config.id);
    });

    if (this.configElement !== undefined) {
      this.configElement.configList = this.configList;
    }
  }

  /**
   * Called every time openConfig is updated
   * @category Watch handler
   */
  @Watch("openConfig")
  openConfigChange(newValue: boolean): void {
    if (this.configElement !== undefined) {
      this.configElement.open = newValue;
    }
    if (this.chartAuthoringActionElement !== undefined) {
      this.createOrUpdateChartAuthoringAction();
    }
    if (this.configReferenceElement !== undefined) {
      if (newValue) {
        this.configReferenceElement.style.visibility = "visible";
      } else {
        this.configReferenceElement.style.visibility = "hidden";
      }
    }
  }

  /**
   * Called every time the `layer` prop is updated
   * @category Watch handler
   */
  @Watch("layer")
  async layerChange(newValue: Sublayer | SupportedLayer | undefined): Promise<void> {
    if (newValue !== undefined) {
      if (newValue.type === "sublayer") {
        this.featureLayer = await newValue.createFeatureLayer();
      } else {
        this.featureLayer = newValue;
      }
    }
  }

  /**
   * Called every time the `featureLayer` state is updated
   * @category Watch handler
   */
  @Watch("featureLayer")
  async featureLayerChange(newValue: SupportedLayer | undefined, oldValue: SupportedLayer | undefined): Promise<void> {
    if (newValue !== undefined && !isEqual(oldValue, newValue)) {
      // remove the tab from the previous layer if using tab manager
      if (this.tabManagerElement !== undefined) {
        await (this.tabManagerElement as any).removeTab(this.chartTabId);
      }

      // clear selection on old feature layer
      if (oldValue !== undefined) {
        this.handleSelectionChange({}, oldValue);

        // close the chart
        this.closeChart();
      }

      if (Array.isArray((newValue as { charts?: WebMapWebChart[] })?.charts)) {
        this.configList = (newValue as { charts?: WebMapWebChart[] })?.charts ?? [];
      } else {
        this.configList = [];
      }

      // reset the panel number to 0 so that the flow starts from the beginning for the new layer
      this.panelNumber = 0;

      const reactiveUtils = await importCoreReactiveUtils();

      // add a watch handler on the "definitionExpression" property of the Layer
      this.layerFilterListener?.remove();
      this.layerFilterListener = reactiveUtils.watch(
        () => getMainLayer(this.featureLayer)?.definitionExpression,
        async (): Promise<void> => {
          const definitionExpression = getMainLayer(this.featureLayer)?.definitionExpression;
          // update the series now that the filter has changed
          if (isBarChartModel(this.model) || isLineChartModel(this.model) || isBoxPlotModel(this.model)) {
            this.model.updateSeriesFromLayerFilter();
            this.dataUpdateNeeded = true;
            let dataFilter = undefined;
            // NOTE: we only support `where` clause for now in MV
            if (definitionExpression) {
              dataFilter = {
                ...this.model.getDataFilter(),
                where: definitionExpression,
              };
            }
            await this.model.setDataFilter(dataFilter);
          }
        },
      );
    }
    if (this.configElement !== undefined) {
      this.configElement.layer = newValue;
    }
  }

  /**
   * Called every time view is updated
   * @category Watch handler
   */
  @Watch("view")
  async viewChange(): Promise<void> {
    this.viewTimeZoneListener?.remove();
    this.viewTimeZoneListener = await this.addViewTimeZoneListener();
  }

  /**
   * Called every time disableUpScaleAction is updated
   * @category Watch handler
   */
  @Watch("disableUpScaleAction")
  disableUpScaleActionChange(newValue: boolean): void {
    this.chartPanel?.childNodes.forEach((scaleAction: HTMLCalciteActionElement) => {
      if (scaleAction.dataset.direction === HeightScaleDirections.Up) {
        const action = scaleAction;
        action.disabled = newValue;
      }
    });
  }

  /**
   * Called every time disableDownScaleAction is updated
   * @category Watch handler
   */
  @Watch("disableDownScaleAction")
  disableDownScaleActionChange(newValue: boolean): void {
    this.chartPanel?.childNodes.forEach((scaleAction: HTMLCalciteActionElement) => {
      if (scaleAction.dataset.direction === HeightScaleDirections.Down) {
        const action = scaleAction;
        action.disabled = newValue;
      }
    });
  }

  /**
   * Called every time panel number is updated
   * @category Watch handler
   */
  @Watch("panelNumber")
  panelNumberChange(newValue: number): void {
    if (this.configElement !== undefined) {
      this.configElement.panelNumber = newValue;
    }
    this.createOrUpdateChartAuthoringAction();

    // if the panelNumber is 0 and the config is partial, then delete the chart (meaning dont save the chart in the configList)
    const currentConfig = this.model?.getConfig();
    if (this.panelNumber === 0 && isPartialConfig(currentConfig)) {
      const deleteEvent = new CustomEvent<ChartDeletePayload>("arcgisChartsConfigChartDelete", {
        detail: { chartId: currentConfig?.id ?? "" },
      });
      this.chartDelete(deleteEvent);
    }
  }

  /**
   * Verify if the selection has changed
   * Notify consumer with same.
   */
  @Watch("selectionData")
  selectionChange(): void {
    const emptySelection = this.isSelectionEmpty();

    if (emptySelection && this.highlightSelect !== undefined) {
      this.highlightSelect.remove();
    }

    // Updating the components property
    if (this.chartElement !== undefined) {
      if (hasSelectionChanged(this.selectionData, this.chartElement.selectionData)) {
        // removing the selection indexes as they are not necessary through ac-app and can disturb the selection
        this.chartElement.selectionData = {
          ...this.selectionData,
          selectionIndexes: undefined,
        };
      }
    }

    // handle FBS and FBE appearance when the selectionData changes
    if (this.actionBarElement !== undefined) {
      // we disable the action when the selection is empty and the filter by selection is not activated, or when the chart is a pie from fields
      // that allows the user to get out of the FBS mode (see issue https://devtopia.esri.com/WebGIS/arcgis-charts/issues/5101)
      if (
        (emptySelection &&
          this.actionBarElement?.filterBySelectionToggle !==
            ActionToggle.Active) /** we disable the FBS when  filterBySelectionToggle is set to `ActionToggle.Active` or `undefined`*/ ||
        this.actionBarElement?.filterByExtentToggle === ActionToggle.Active ||
        getChartSubType(this.model?.getConfig().series ?? []) === ChartSubType.PieFromFields
      ) {
        this.actionBarElement.filterBySelectionState = ActionState.Disabled;
      } else {
        this.actionBarElement.filterBySelectionState = ActionState.Enabled;
      }

      // enable/disabled FBE button when the selection is non-empty and FBS button is off
      if (!emptySelection && this.actionBarElement.filterBySelectionToggle === ActionToggle.Inactive) {
        this.actionBarElement.filterByExtentState = ActionState.Enabled;
      }
    }

    this.arcgisChartsAppSelectionChange.emit(this.selectionData);
  }

  /**
   * Called every time disableEditAction is updated
   * @category Watch handler
   */
  @Watch("disableEditAction")
  disableEditActionChange(newValue: boolean | undefined): void {
    if (this.chartAuthoringActionElement !== undefined) {
      this.chartAuthoringActionElement.disabled = newValue === true;
    }
  }

  /**
   * Called every time actionBarHideActionsProps is updated
   * @param newValue
   */
  @Watch("actionBarHideActionsProps")
  onActionBarHideActionsPropsChange(newValue: ActionBarHideActionsProps): void {
    if (this.actionBarElement !== undefined) {
      this.actionBarElement.actionBarHideActionsProps = { ...newValue };
    }
  }

  /**
   * Called every time modelLoading is updated
   * @category Watch handler
   */
  @Watch("modelLoading")
  modelLoadingChange(): void {
    if (this.configElement !== undefined) {
      this.configElement.modelLoading = this.modelLoading;
    }
  }

  /**
   * charts config change event when a chart is added or updated.
   * @event
   */
  // prettier-ignore
  @Event({
    eventName: "arcgisChartsAppChartsConfigChange",
    composed: true,
    cancelable: true,
    bubbles: true
  })
  arcgisChartsAppChartsConfigChange: EventEmitter;

  /**
   * charts list change event when a chart is deleted from the charts list.
   * @event
   */
  // prettier-ignore
  @Event({ eventName: "arcgisChartsAppChartsListChange", composed: true, cancelable: true, bubbles: true }) arcgisChartsAppChartsListChange: EventEmitter;

  /**
   * charts config change event when the chart config panel is closed
   * @event
   */
  // prettier-ignore
  @Event({ eventName: "arcgisChartsAppChartsConfigClose", composed: true, cancelable: true, bubbles: true }) arcgisChartsAppChartsConfigClose: EventEmitter;

  /**
   * event when chart panel "scale" action is clicked
   * @event
   */
  // prettier-ignore
  @Event({ eventName: "arcgisChartsAppChartScaleChange", composed: true, cancelable: true, bubbles: true }) arcgisChartsAppChartScaleChange: EventEmitter<HeightScaleDirections>;

  /**
   * event when chart panel "close" action is clicked
   * @event
   */
  // prettier-ignore
  @Event({ eventName: "arcgisChartsAppChartClose", composed: true, cancelable: true, bubbles: true }) arcgisChartsAppChartClose: EventEmitter;

  /**
   * event when chart panel "open" action is clicked
   * @event
   */
  // prettier-ignore
  @Event({ eventName: "arcgisChartsAppChartsConfigOpen", composed: true, cancelable: true, bubbles: true }) arcgisChartsAppChartsConfigOpen: EventEmitter;

  /**
   * event when selection changed.
   * @event
   */
  // prettier-ignore
  @Event({ eventName: "arcgisChartsAppSelectionChange", composed: true, cancelable: true, bubbles: true }) arcgisChartsAppSelectionChange: EventEmitter<SelectionData>;

  /**
   * Method used to set the focus to the config component
   * @category Public
   */
  @Method()
  public async setFocusToConfig(): Promise<void> {
    this.configElement?.setFocus();
  }

  /**
   * @category Lifecycle
   */
  public async componentWillLoad(): Promise<void> {
    await this.layerChange(this.layer);
    await this.selectedChartIdChange();
    this.tabManagerChange(this.tabManagerElement);
    this.configReferenceChange(this.configReferenceElement);
    this.chartReferenceChange(this.chartReferenceElement);
    this.disableEditActionChange(this.disableEditAction);
    this.configListChange();
  }

  /**
   * @category Lifecycle
   */
  disconnectedCallback(): void {
    this.clearSelection();
    this.highlightSelect?.remove();
    this.configElement?.remove();
    this.chartAuthoringActionElement?.remove();
    this.actionBarElement?.remove();
    this.chartElement?.remove();
    this.chartPanel?.remove();
    this.chartPanel = undefined;
    this.layerFilterListener?.remove();
    this.viewTimeZoneListener?.remove();
    (this.tabManagerElement as any)?.removeTab(this.chartTabId);
  }

  /**
   * Indicates whether the current selection is empty
   */
  private isSelectionEmpty(): boolean {
    return this.selectionData?.selectionOIDs === undefined || this.selectionData.selectionOIDs.length === 0;
  }

  /**
   * Creates a new configReferenceElement inside host element
   * @category Private
   */
  private createConfigReferenceElement(): void {
    this.configReferenceElement = document.createElement("div");
    this.configReferenceElement.classList.add(HTMLClasses.chartConfig);
    this.hostElement.shadowRoot?.appendChild(this.configReferenceElement);
  }

  /**
   * Creates a new chartReferenceElement inside host element
   * @category Private
   */
  private createChartReferenceElement(): void {
    this.chartReferenceElement = document.createElement("div");
    this.chartReferenceElement.classList.add(HTMLClasses.chartComponent);
    this.hostElement.shadowRoot?.appendChild(this.chartReferenceElement);
  }

  /**
   * Creates chart model to use for config interaction.
   * @category Private
   */
  private async createModel(props: {
    chartType?: ChartTypes;
    config?: WebMapWebChart;
    overwriteId?: string;
    pieMode?: PieChartDataModes;
  }): Promise<void> {
    const { chartType, config, pieMode } = props;
    const overwriteId = props.overwriteId ?? "";

    switch (chartType) {
      case WebChartTypes.BarSeries: {
        if (config === undefined || isChartType<ChartConfig<WebChartBarChartSeries, WebChart>>(config, chartType)) {
          this.model = new BarChartModel();
          await this.model.setup({
            layer: this.featureLayer,
            config,
          });
        }
        break;
      }
      case WebChartTypes.LineSeries: {
        if (config === undefined || isChartType<ChartConfig<WebChartLineChartSeries, WebChart>>(config, chartType)) {
          this.model = new LineChartModel();
          await this.model.setup({
            layer: this.featureLayer,
            config,
          });
        }
        break;
      }
      case WebChartTypes.PieSeries: {
        if (config === undefined || isChartType<ChartConfig<WebChartPieChartSeries, WebChart>>(config, chartType)) {
          this.model = new PieChartModel();

          // initialize the pie chart mode
          if (isPieChartModel(this.model) && pieMode !== undefined) {
            this.model.setMode(pieMode);
          }

          await this.model.setup({
            layer: this.featureLayer,
            config,
          });
        }
        break;
      }
      case WebChartTypes.HistogramSeries: {
        if (config === undefined || isChartType<ChartConfig<WebChartHistogramSeries, WebChart>>(config, chartType)) {
          this.model = new HistogramModel();
          await this.model.setup({
            layer: this.featureLayer,
            config,
          });
        }
        break;
      }
      case WebChartTypes.ScatterSeries: {
        if (config === undefined || isChartType<ChartConfig<WebChartScatterPlotSeries, WebChart>>(config, chartType)) {
          this.model = new ScatterPlotModel();
          await this.model.setup({
            layer: this.featureLayer,
            config,
          });
        }
        break;
      }
      case WebChartTypes.BoxPlotSeries: {
        if (
          config === undefined ||
          isChartType<ChartConfig<WebChartBoxPlotSeries | WebChartLineChartSeries, WebChart>>(config, chartType)
        ) {
          this.model = new BoxPlotModel();
          await this.model.setup({
            layer: this.featureLayer,
            config,
          });
        }
        break;
      }
      default: {
        this.model = undefined;
        break;
      }
    }

    // if an `overwriteId` is provided, this will cause the config with that id to be overwritten by this new config
    if (overwriteId !== "") {
      const configToOverwrite = this.configList.find((eachConfig) => eachConfig.id === overwriteId);
      const modelConfigId = this.model?.getConfig().id;
      if (configToOverwrite?.id !== undefined && modelConfigId !== undefined) {
        configToOverwrite.id = modelConfigId;
        this.arcgisChartsAppChartsListChange.emit({
          chartId: overwriteId,
          isDelete: true,
        });
      }
    }

    // add event listeners for the chart model
    this.addModelListeners();

    // add model to config element
    if (this.configElement !== undefined) {
      this.configElement.model = this.model;
    }

    // call model change
    await this.modelConfigChange();
  }

  /**
   * Adds event listeners for chart model
   * @category Private
   */
  private addModelListeners(): void {
    if (this.configElement !== undefined) {
      // Map object to hold event names and handlers
      const events = new Map<string, (e: CustomEvent) => void>();

      // Model config change
      events.set("arcgisChartsModelConfigChange", this.modelConfigChange);

      // Data change
      events.set("arcgisChartsModelDataChange", this.modelDataChange);

      if (this.model !== undefined) {
        // Add all event listeners to chart model
        addEventListenersFromMap(this.model, events);
      }
    }
  }

  /**
   * Creates configElement appended to configReferenceElement
   * @category Private
   */
  private createConfigElement(): void {
    // Remove old configElement
    this.configElement?.remove();

    this.panelNumber = this.configElement?.panelNumber ?? 0;
    // Create new configElement
    this.configElement = document.createElement("arcgis-charts-config-create-flow");
    this.configElement.model = this.model;
    this.configElement.selectedChartId = this.selectedChartId;
    if (this.usePopupTemplateFieldsInfo !== undefined) {
      this.configElement.usePopupTemplateFieldsInfo = this.usePopupTemplateFieldsInfo;
    }
    if (this.featureLayer !== undefined) {
      this.configElement.layer = this.featureLayer;
    }
    if (this.configList !== undefined) {
      this.configElement.configList = this.configList;
    }
    if (this.configHeaderTitle !== undefined) {
      this.configElement.headerTitle = this.configHeaderTitle;
    }
    // Set popover placement
    this.configElement.popoverPlacement = this.popoverPlacement;
    this.configElement.open = true;
    this.configElement.panelNumber = this.panelNumber ?? 0;
    this.configElement.r2 = this.r2;
    this.configElement.chartData = this.chartData;
    this.addConfigEventListeners();
    this.addModelListeners();
    this.configReferenceElement?.appendChild(this.configElement);
  }

  /**
   * Adds event listeners for config element
   * @category Private
   */
  private addConfigEventListeners(): void {
    if (this.configElement !== undefined) {
      // Map object to hold event names and handlers
      const events = new Map<string, (e: CustomEvent) => void>();

      // No series change
      events.set("arcgisChartsConfigNoSeriesUpdate", this.chartConfigNoSeriesUpdate);

      // Web chart change
      events.set("arcgisChartsConfigSelectedChartChange", this.configSelectedChartChange);

      // Chart config delete
      events.set("arcgisChartsConfigChartDelete", this.chartDelete);

      // Chart title change
      events.set("arcgisChartsConfigChartRenamePopoverChange", this.chartRename);

      // Listens to data change error event to bubble up from config UI.
      events.set("arcgisChartsConfigDataChangeError", this.showChartWarning);
      events.set("arcgisChartsConfigPopoverOpenChange", this.changePopoverOpenChange);

      // Chart config close
      events.set("arcgisChartsConfigChartViewChartClose", this.chartConfigClose);
      events.set("arcgisChartsConfigPanelNumberChange", this.panelNumberUpdate);

      // Line Chart time interval changes
      events.set("arcgisChartsConfigCustomizedTimeInterval", this.lineChartCustomizedTimeInterval);
      events.set("arcgisChartsConfigResetTimeInterval", this.lineChartResetTimeInterval);

      // Pie Chart mode change
      events.set("arcgisChartsConfigPieModeChange", this.pieModeChange);

      // Add all event listeners to config element
      addEventListenersFromMap(this.configElement, events);
    }
  }

  /**
   * Creates chartElement appended to chartReferenceElement
   * @category Private
   */
  private async createChartElement(): Promise<void> {
    if (this.strings === undefined) {
      ({ strings: this.strings } = await fetchComponentLocaleStrings(this.hostElement, getAssetPath(`.`)));
    }
    await this.model?.setupFinished;
    const config = this.model?.getConfig();
    const currentChartType = this.model?.getChartType();
    let placeholder: string | undefined;
    let disableTogglingLegendItems: boolean | undefined = false;
    let layerFilterChangePolicy: LayerFilterChangePolicy = LayerFilterChangePolicy.Refresh;
    // Remove old chartElement
    this.chartElement?.remove();

    // Create new chartElement based on current chart type
    switch (currentChartType) {
      case WebChartTypes.BarSeries:
        this.chartElement = document.createElement(
          this.enableChartBetaComponents ? "arcgis-charts-bar-chart-beta" : "arcgis-charts-bar-chart",
        );
        disableTogglingLegendItems = this.barChartDisableTogglingLegendItems;
        placeholder = this.strings?.barChartCreateMsg;
        layerFilterChangePolicy = LayerFilterChangePolicy.Ignore;
        break;
      case WebChartTypes.BoxPlotSeries:
        this.chartElement = document.createElement("arcgis-charts-box-plot");
        disableTogglingLegendItems = this.boxPlotDisableTogglingLegendItems;
        placeholder = this.strings?.boxPlotCreateMsg;
        layerFilterChangePolicy = LayerFilterChangePolicy.Ignore;
        break;
      case WebChartTypes.LineSeries:
        this.chartElement = document.createElement(
          this.enableChartBetaComponents ? "arcgis-charts-line-chart-beta" : "arcgis-charts-line-chart",
        );
        disableTogglingLegendItems = this.lineChartDisableTogglingLegendItems;
        placeholder = this.strings?.lineChartCreateMsg;
        layerFilterChangePolicy = LayerFilterChangePolicy.Ignore;
        break;
      case WebChartTypes.PieSeries: {
        this.chartElement = document.createElement("arcgis-charts-pie-chart");
        disableTogglingLegendItems = this.pieChartDisableTogglingLegendItems;
        placeholder = this.strings?.pieChartCreateMsg;
        break;
      }
      case WebChartTypes.HistogramSeries:
        this.chartElement = document.createElement("arcgis-charts-histogram");
        disableTogglingLegendItems = this.histogramDisableTogglingLegendItems;
        placeholder = this.strings?.histogramCreateMsg;
        break;
      case WebChartTypes.ScatterSeries:
        this.chartElement = document.createElement(
          this.enableChartBetaComponents ? "arcgis-charts-scatter-plot-beta" : "arcgis-charts-scatter-plot",
        );
        disableTogglingLegendItems = this.scatterPlotDisableTogglingLegendItems;
        placeholder = this.strings?.scatterPlotCreateMsg;
        break;
      default:
        placeholder = undefined;
        break;
    }

    if (this.chartElement !== undefined) {
      this.chartElement.autoDisposeChart = false;
      this.chartElement.disableTogglingLegendItems = disableTogglingLegendItems;
      this.chartElement.layerFilterChangePolicy = layerFilterChangePolicy;

      if (this.usePopupTemplateFieldsInfo !== undefined) {
        this.chartElement.usePopupTemplateFieldsInfo = this.usePopupTemplateFieldsInfo;
      }

      if (hasSelectionChanged(this.selectionData, this.chartElement.selectionData)) {
        this.chartElement.selectionData = {
          ...this.selectionData,
          selectionSource: SelectionSource.ProgrammaticSelection,
        };
      }
      this.chartElement.placeholder = placeholder;
      // To fix the Safari Chart squished issue, Safari's has different defaults for flex grow, shrink and basis.
      if (this.chartElement?.style !== undefined) {
        this.chartElement.style.flex = "1 0 auto";
      }

      if (currentChartType === WebChartTypes.LineSeries) {
        (this.chartElement as HTMLArcgisChartsLineChartElement).setTimeBinningInfoWhenNotProvided = true;
      }

      if (config !== undefined) {
        // Set chart config with new object to trigger render on chartElement
        this.chartElement.config = this.isCreate ? undefined : cloneDeep(config);

        this.createActionBarElement(this.actionsReferenceElement !== undefined);
      } else {
        this.actionBarElement?.removeEventListener(
          ChartComponentEvents.DefaultActionSelect,
          this.onDefaultActionSelect,
        );
        this.actionBarElement?.remove();
        this.actionBarElement = undefined;
      }
      if (this.featureLayer !== undefined) {
        this.chartElement.layer = this.featureLayer;
      }

      // ensure the view time zone listener gets set up after the `chartElement` has been created
      this.viewChange();

      // checking whether the "timeZone" property exists on the view as it's currently available only on the MapView and not the SceneView
      if (this.view !== undefined && "timeZone" in this.view && this.view.timeZone !== undefined) {
        this.viewTimeZoneChange();
      }

      this.addChartEventListeners();

      // Create calcite-panel wrapper for chart component
      if (this.tabManagerElement !== undefined) {
        this.chartReferenceElement?.appendChild(this.chartElement);
        this.createChartTab();
      } else {
        this.createChartPanel();
      }

      // Specific settings for table layers
      if (getMainLayer(this.featureLayer)?.isTable) {
        // we disable all actions on the chart.
        // Note: the zoom could be interesting on a table layer, so when another consumer starts using ac-app we might expose instead
        // a new @Prop at the ac-app component level to allow the consumer choosing the default action mode.
        if ("actionMode" in this.chartElement) {
          (this.chartElement as HTMLArcgisChartsZoomableChartElement).actionMode = ActionModes.None;
        }

        // setting the action bar
        this.setActionBarForTableLayer();
      }

      // reset `modelLoading` to false since the model has finished loading
      this.modelLoading = false;
    }
  }

  /**
   * Shows a chart message
   * @category Private
   */
  private showChartWarning = (e: CustomEvent): void => {
    this.setForceDisableActions(true);
    (this.chartElement as HTMLArcgisChartsBarChartElement)?.errorAlert(e.detail);
  };

  /**
   * Creates a tooltip for the given reference element
   * @category Private
   */
  private createTooltip = (referenceElement: HTMLElement, text: string): void => {
    const tooltip = document.createElement("calcite-tooltip");
    tooltip.style.whiteSpace = "nowrap";
    tooltip.placement = "bottom";
    tooltip.open = true;
    tooltip.label = text;
    tooltip.textContent = text;
    tooltip.referenceElement = referenceElement;
    referenceElement.appendChild(tooltip);
  };

  /**
   * Removes a tooltip for the given reference element
   * @category Private
   */
  private removeTooltip = (referenceElement: HTMLElement): void => {
    referenceElement.querySelector("calcite-tooltip")?.remove();
  };

  /**
   * Creates calcite-panel wrapper element for chart component
   * @category Private
   */
  private async createChartPanel(): Promise<void> {
    this.chartPanel?.remove();
    this.chartPanel = document.createElement("calcite-panel");
    this.chartPanel.style.height = "100%";
    this.chartPanel.closable = this.chartPanelClosable ?? true;

    // Add heading
    this.chartPanel.heading = this.chartHeaderTitle ?? (await this.getCurrentChartTypeText());

    // Add scale actions
    if (this.chartPanelScalable) {
      // up scale action
      const upScaleAction = document.createElement("calcite-action");
      upScaleAction.icon = "chevrons-up";
      upScaleAction.slot = "header-actions-end";
      upScaleAction.disabled = this.disableUpScaleAction;
      upScaleAction.dataset.direction = HeightScaleDirections.Up;
      upScaleAction.label = this.strings?.expand;

      // add a tooltip upon hover
      upScaleAction.addEventListener("mouseenter", () => this.createTooltip(upScaleAction, this.strings?.expand));

      // remove the tooltip when the mouse leaves the action
      upScaleAction.addEventListener("mouseleave", () => {
        this.removeTooltip(upScaleAction);
      });

      this.chartPanel.appendChild(upScaleAction);
      upScaleAction.addEventListener("click", () => {
        this.arcgisChartsAppChartScaleChange.emit(HeightScaleDirections.Up);
      });

      // down scale action
      const downScaleAction = document.createElement("calcite-action");
      downScaleAction.icon = "chevrons-down";
      downScaleAction.slot = "header-actions-end";
      downScaleAction.disabled = this.disableDownScaleAction;
      downScaleAction.dataset.direction = HeightScaleDirections.Down;
      downScaleAction.label = this.strings?.collapse;

      // add a tooltip upon hover
      downScaleAction.addEventListener("mouseenter", () => this.createTooltip(downScaleAction, this.strings?.collapse));

      // remove the tooltip when the mouse leaves the action
      downScaleAction.addEventListener("mouseleave", () => {
        this.removeTooltip(downScaleAction);
      });

      this.chartPanel.appendChild(downScaleAction);
      downScaleAction.addEventListener("click", () => {
        this.arcgisChartsAppChartScaleChange.emit(HeightScaleDirections.Down);
      });
    }

    // Listen for panel close
    this.chartPanel.addEventListener("calcitePanelClose", this.handleChartClose);

    // Add chart element to panel
    if (this.chartPanel !== undefined && this.chartElement !== undefined) {
      this.chartPanel.appendChild(this.chartElement);
      this.chartReferenceElement?.appendChild(this.chartPanel);
    }
  }

  /**
   * Creates a new chart tab via the tab manager.
   * @category Private
   */
  private async createChartTab(): Promise<void> {
    if (this.chartReferenceElement !== undefined) {
      this.chartReferenceElement.style.height = "100%";
    }

    const config = this.model?.getConfig();
    const tabTitle = isPartialConfig(config) ? await this.getCurrentChartTypeText() : config?.title?.content.text;

    // Add chart tab to tab manager
    const { title } =
      (await (this.tabManagerElement as any)?.addTab({
        title: {
          title: this.chartHeaderTitle ?? tabTitle,
          iconStart: this.model ? getChartIcon(this.model?.getConfig()) : "graph-bar",
        },
        content: this.chartReferenceElement,
        id: this.chartTabId,
      })) ?? {};

    // Set tab title element
    this.tabTitleElement = title as HTMLCalciteTabTitleElement;
    this.addTabTitleEventListeners();
  }

  /**
   * Adds event listeners for tab title element
   * @category Private
   */
  private addTabTitleEventListeners(): void {
    // Add event listener to tab title element for when the tab is closed
    this.tabTitleElement?.removeEventListener("calciteTabsClose", this.handleChartClose);
    this.tabTitleElement?.addEventListener("calciteTabsClose", this.handleChartClose);
  }

  /**
   * Adds event listeners for chart element
   * @category Private
   */
  private addChartEventListeners(): void {
    const currentChartType = this.model?.getChartType();
    if (this.chartElement !== undefined) {
      // Map object to hold event names and handlers
      const events = new Map<string, (e: CustomEvent) => void>();

      // Data process complete
      events.set(ChartComponentEvents.DataProcessComplete, this.dataProcessComplete);

      // Axes min/max change
      events.set(ChartComponentEvents.AxesMinMaxChange, this.axesMinMaxChange);

      // Series color change
      if (
        currentChartType === WebChartTypes.BarSeries ||
        currentChartType === WebChartTypes.BoxPlotSeries ||
        currentChartType === WebChartTypes.LineSeries ||
        currentChartType === WebChartTypes.PieSeries ||
        currentChartType === WebChartTypes.HistogramSeries
      ) {
        events.set(ChartComponentEvents.SeriesColorChange, this.seriesColorChange);
      }

      // Update complete
      events.set(ChartComponentEvents.UpdateComplete, this.updateComplete);

      // listening for selection event
      events.set(ChartComponentEvents.SelectionComplete, (e: CustomEvent<SelectionData>) => {
        this.handleSelectionChange(e.detail, this.featureLayer);
      });

      // No-render prop changed
      events.set(ChartComponentEvents.NoRenderPropChange, this.noRenderPropChange);

      // When a series or overlay is toggled from the legend
      if (currentChartType !== WebChartTypes.PieSeries) {
        events.set(ChartComponentEvents.LegendItemVisibilityChange, this.legendItemVisibilityChange);
      }

      // Add all event listeners to chart element
      addEventListenersFromMap(this.chartElement, events);
    }
  }

  /**
   * Set Action mode to selection by default else whatever user selects.
   */
  private setActionMode(): void {
    if (this.chartElement !== undefined && this.actionBarElement !== undefined) {
      switch ((this.chartElement as HTMLArcgisChartsZoomableChartElement).actionMode) {
        case ActionModes.Zoom:
          if ("actionMode" in this.chartElement) {
            this.actionBarElement.zoomToggle = ActionToggle.Active;
          }
          break;
        case ActionModes.MonoSelection:
        case ActionModes.MultiSelection:
        case ActionModes.MultiSelectionWithCtrlKey:
        default:
          this.actionBarElement.selectionToggle = ActionToggle.Active;
          break;
      }
    }
  }

  private setActionBarActions(): void {
    if (this.actionBarElement !== undefined) {
      this.setForceDisableActions();
      this.setLegendActionState();
      this.setRotateActionState();
      this.setClearSelectionActionState();
      this.setZoomAndSelectionActionStates();

      // Disable all selection actions for chart from table
      if (getMainLayer(this.featureLayer)?.isTable) {
        this.disableActionsForTableLayer();
      }

      this.actionBarHideActionsProps = {
        ...this.actionBarHideActionsProps,
        hideFilterByExtent: false,
        hideFilterBySelection: false,
      };
    }
  }

  /**
   * Creates actionBarElement to be appended to chartElement
   * @category Private
   */
  private createActionBarElement(useReference: boolean): void {
    this.actionBarElement?.removeEventListener(ChartComponentEvents.DefaultActionSelect, this.onDefaultActionSelect);
    this.actionBarElement?.remove();
    this.actionBarElement = document.createElement("arcgis-charts-action-bar");

    this.setActionBarActions();

    this.actionBarElement.expanded = this.actionBarExpanded;

    this.actionBarElement.addEventListener(ChartComponentEvents.DefaultActionSelect, this.onDefaultActionSelect);
    this.actionBarElement.addEventListener(ChartComponentEvents.ActionBarToggle, this.onActionBarToggle);

    this.createOrUpdateChartAuthoringAction();

    if (this.chartElement !== undefined) {
      if (useReference) {
        this.actionsReferenceElement?.appendChild(this.actionBarElement);
      } else {
        this.actionBarElement.slot = "action-bar";
        this.chartElement.appendChild(this.actionBarElement);
      }
    }

    this.selectionChange();

    this.setActionMode();
  }

  /**
   * Disables all action bar actions related to selection
   * @category Private
   */
  private disableActionsForTableLayer(): void {
    if (this.actionBarElement !== undefined) {
      this.actionBarElement.clearSelectionState = ActionState.Disabled;
      this.actionBarElement.filterBySelectionState = ActionState.Disabled;
      this.actionBarElement.selectionState = ActionState.Disabled;
      this.actionBarElement.switchSelectionState = ActionState.Disabled;
      this.actionBarElement.filterByExtentState = ActionState.Disabled;
    }
  }

  /**
   * Configures the action bar for a table layer
   * @category Private
   */
  private setActionBarForTableLayer(): void {
    if (this.actionBarElement !== undefined) {
      this.disableActionsForTableLayer();

      this.actionBarHideActionsProps = {
        ...this.actionBarHideActionsProps,
        hideFilterByExtent: true,
      };
      this.actionBarElement.zoomToggle = ActionToggle.Active;
    }
  }

  /**
   * Sets the disabled and active state of the legend in action bar
   * @category Private
   */
  private setLegendActionState(): void {
    const config = this.model?.getConfig();
    if (config?.series !== undefined && this.actionBarElement !== undefined) {
      const canShow = canShowLegend(config.series);
      this.actionBarElement.legendState = !canShow ? ActionState.Disabled : ActionState.Enabled;
      this.actionBarElement.legendToggle =
        canShow && config.legend?.visible ? ActionToggle.Active : ActionToggle.Inactive;
    }
  }

  /**
   * Sets the hidden and active state of rotate in action bar
   * @category Private
   */
  private setRotateActionState(): void {
    const config = this.model?.getConfig();
    if (config?.series[0] !== undefined && this.actionBarElement !== undefined) {
      const currentChartType = this.model?.getChartType();
      const hideRotateChart =
        currentChartType !== WebChartTypes.BarSeries &&
        currentChartType !== WebChartTypes.LineSeries &&
        currentChartType !== WebChartTypes.BoxPlotSeries;

      this.actionBarHideActionsProps = {
        ...this.actionBarHideActionsProps,
        hideRotateChart,
      };

      this.actionBarElement.rotateChartToggle = isChartRotated(config) ? ActionToggle.Active : ActionToggle.Inactive;
    }
  }

  /**
   * Sets the hidden state for the zoom, selection, and full extent in the action bar
   * @category Private
   */
  private setZoomAndSelectionActionStates(): void {
    if (this.actionBarElement !== undefined) {
      this.actionBarHideActionsProps = {
        ...this.actionBarHideActionsProps,
        hideZoom: true,
        hideFullExtent: true,
        hideSelection: true,
      };
    }
  }

  /**
   * Sets the disabled and active state of "Clear Selection" in the action bar
   * @category Private
   */
  private setClearSelectionActionState(): void {
    if (this.actionBarElement !== undefined) {
      this.actionBarElement.clearSelectionState = this.isSelectionEmpty() ? ActionState.Disabled : ActionState.Enabled;
    }
  }

  /**
   * Sets action bar `forceDisableActions` prop via parameter if provided, or based on chart validity.
   * @category Private
   */
  private async setForceDisableActions(forceDisable?: boolean): Promise<void> {
    const config = this.model?.getConfig();

    const currentChartType = this.model?.getChartType();
    const convertedConfig = await convertChartSpec(config);
    const fullConfig = addILayer({
      chartConfig: convertedConfig,
      queryObject: this.featureLayer,
    });
    const chartValidation = await validateWebChart({
      input: fullConfig,
      chartType: currentChartType,
      queryObject: this.featureLayer,
    });
    const disable = forceDisable ?? (!chartValidation.valid || isPartialConfig(fullConfig));

    if (this.actionBarElement !== undefined) {
      this.actionBarElement.forceDisableActions = disable;
    }
  }

  /**
   * Creates or updates custom Chart Authoring action for chartElement's action bar
   * @category Private
   */
  private async createOrUpdateChartAuthoringAction(): Promise<void> {
    if (this.strings === undefined) {
      ({ strings: this.strings } = await fetchComponentLocaleStrings(this.hostElement, getAssetPath(`.`)));
    }
    if (this.chartAuthoringActionElement === undefined) {
      this.chartAuthoringActionElement = document.createElement("calcite-action");
      this.chartAuthoringActionElement.slot = "custom-action-top";
      this.chartAuthoringActionElement.icon = "gear";
      this.chartAuthoringActionElement.text = this.strings?.editChart;
      this.chartAuthoringActionElement.onclick = this.onOpenChartConfigClick;
    }
    if (this.actionBarElement !== undefined) {
      this.actionBarElement.appendChild(this.chartAuthoringActionElement);
    }
    this.chartAuthoringActionElement.active = this.openConfig && this.panelNumber !== 0;
    this.chartAuthoringActionElement.disabled = this.disableEditAction;
  }

  /**
   * @category Event handler
   */
  private chartConfigNoSeriesUpdate = (): void => {
    this.chartElement?.notify(this.strings?.invalidSeriesConfig, this.strings?.invalidSeriesConfigTitle);
  };

  /**
   * @category Event handler
   */
  private modelConfigChange = debounce(async (e?: CustomEvent<ModelConfigChangePayload>): Promise<void> => {
    const config = e?.detail.config ?? this.model?.getConfig();
    this.selectedChartId = config?.id;

    // TODO: now featureLayer.charts is available we can update charts property of featureLayer directly here also.
    // Handle it as new issue, currently MVB uses configList Prop to send and read the charts list.
    const selectedChartIndex = this.configList.findIndex((eachConfig: WebChart) => eachConfig.id === config?.id);

    if (Array.isArray(this.configList) && config !== undefined && !isPartialConfig(config)) {
      if (selectedChartIndex !== -1) {
        this.configList[selectedChartIndex] = config;
        this.configList = [...this.configList];
      } else {
        this.configList = [...this.configList, config];
      }
    }

    if (this.chartReferenceElement !== undefined) {
      this.chartReferenceElement.hidden = false;
    }
    if (this.chartPanel !== undefined) {
      this.chartPanel.closed = false;
    }

    if (this.chartElement !== undefined && this.actionBarElement !== undefined && !this.modelLoading) {
      if (isPartialConfig(config)) {
        // use placeholder text for a partial config
        let placeholder: string | undefined;
        // As the config might be retrieved from the event detail rather than model, we have the fallback option of getChartOrSeriesType
        switch (this.model?.getChartType() ?? getChartOrSeriesType(config?.series)) {
          case WebChartTypes.BarSeries:
            placeholder = this.strings?.barChartCreateMsg;
            break;
          case WebChartTypes.BoxPlotSeries:
            placeholder = this.strings?.boxPlotCreateMsg;
            break;
          case WebChartTypes.LineSeries:
            placeholder = this.strings?.lineChartCreateMsg;
            break;
          case WebChartTypes.PieSeries: {
            placeholder = this.strings?.pieChartCreateMsg;
            break;
          }
          case WebChartTypes.HistogramSeries:
            placeholder = this.strings?.histogramCreateMsg;
            break;
          case WebChartTypes.ScatterSeries:
            placeholder = this.strings?.scatterPlotCreateMsg;
            break;
          default:
            placeholder = undefined;
            break;
        }

        this.chartElement.placeholder = placeholder;
        this.chartElement.config = undefined;
        this.isCreate = true;
      } else {
        this.chartElement.placeholder = undefined;
        this.chartElement.config = cloneDeep(config);
        this.isCreate = false;

        // perform a refresh if a data update is needed
        if (this.dataUpdateNeeded) {
          this.chartElement.refresh();
          this.dataUpdateNeeded = false;
        }
      }
      this.isCreateChange(this.isCreate);

      this.setActionBarActions();
    } else if (
      ((this.chartReferenceElement !== undefined && this.autoReloadNewChartIds) ||
        this.tabManagerElement !== undefined) &&
      this.modelLoading
    ) {
      await this.createChartElement();
    }

    const panelHeadingText = this.chartHeaderTitle ?? (await this.getCurrentChartTypeText());

    // Update chart heading if changed
    if (this.chartPanel !== undefined) {
      if (panelHeadingText !== this.chartPanel.heading) {
        // Set heading
        this.chartPanel.heading = panelHeadingText;
      }
    }

    const tabTitle = isPartialConfig(config) ? await this.getCurrentChartTypeText() : config?.title?.content.text;

    // Update chart tab title if changed
    if (this.tabManagerElement !== undefined && this.tabTitleElement !== undefined) {
      if (tabTitle !== this.tabTitleElement.textContent) {
        // Set tab title
        this.tabTitleElement.textContent = this.chartHeaderTitle ?? tabTitle ?? null;
      }
    }

    if (this.tabManagerElement !== undefined && this.tabTitleElement?.selected === false) {
      (this.tabManagerElement as any).selectTab(this.chartTabId);
    }

    // emit app config change event
    this.arcgisChartsAppChartsConfigChange.emit({
      config: cloneDeep(config),
      isCreate: this.isCreate,
    });
  }, DebounceTimers.ModelConfigChange);

  /**
   * @category Event handler
   */
  private modelDataChange = debounce((): void => {
    if (this.actionBarElement !== undefined) {
      this.actionBarElement.filterBySelectionToggle = ActionToggle.Inactive;
    }

    // Toggling off the filter by selection on the chart component
    if (this.chartElement !== undefined) {
      this.chartElement.filterBySelection = false;
    }

    // Toggling off the filter by selection on the config component
    if (this.configElement !== undefined) {
      this.configElement.filterBySelection = false;
    }

    // setting the hideOversizedStackedLabels to fase for all series in the bar chart model
    if (isBarChartModel(this.model)) {
      for (let i = 0; i < this.model.getSeriesLength(); i += 1) {
        this.model.setSeriesHideOversizedStackedLabels(false, i);
      }
    }
  }, DebounceTimers.ModelConfigChange);

  /**
   * @category Event handler
   */
  private configSelectedChartChange = async (e: CustomEvent): Promise<void> => {
    this.isCreate = e.detail.isCreate;
    this.isCreateChange(this.isCreate);

    if (this.isCreate) {
      const newChartType = e.detail.chartType as ChartTypes;
      this.modelLoading = true;
      await this.createModel({ chartType: newChartType });
    } else {
      this.selectedChartId = e.detail.chartId;
      if (this.tabManagerElement !== undefined && this.tabTitleElement?.selected === false) {
        (this.tabManagerElement as any).selectTab(this.chartTabId);
      }
    }
  };

  /**
   * @category Event handler
   */
  private handleChartClose = (): void => {
    this.closeChart();
    this.arcgisChartsAppChartClose.emit();
  };

  /**
   * @category Event handler
   */
  private chartDelete = (e: CustomEvent<ChartDeletePayload>): void => {
    this.configList = this.configList?.filter((chart) => chart.id !== e.detail.chartId);
    this.model = undefined;
    if (this.selectedChartId === e.detail.chartId) {
      this.selectedChartId = undefined;

      if (this.chartPanel !== undefined) {
        this.chartPanel.closed = true;
        this.handleChartClose();
      }
    }

    this.arcgisChartsAppChartsListChange.emit({
      chartId: e.detail.chartId,
      isDelete: true,
    });
  };

  /**
   * @category Event handler
   */
  private chartRename = (e: CustomEvent): void => {
    const config = this.model?.getConfig();
    if (config?.id === e.detail.chartId && this.model !== undefined) {
      this.model.setTitleText(e.detail.name);
    } else {
      this.configList = this.configList?.map((chart, index) => {
        const newChart = chart;
        if (newChart?.title?.content !== undefined && (chart.id ?? index.toString()) === e.detail.chartId) {
          newChart.title.content.text = e.detail.name;
        }

        return newChart;
      });
    }

    this.arcgisChartsAppChartsConfigChange.emit({
      config: cloneDeep(config),
      isCreate: false,
    });
  };

  /**
   * @category Event handler
   */
  private chartConfigClose = (): void => {
    if (this.chartAuthoringActionElement !== undefined) {
      this.chartAuthoringActionElement.active = false;
      this.openConfig = false;
    }
    this.arcgisChartsAppChartsConfigClose.emit();
  };

  /**
   * @param e The event carrying the panel number
   * @category Event handler
   */
  private panelNumberUpdate = (e: CustomEvent): void => {
    this.panelNumber = e.detail.panelNumber;
  };

  /**
   * @category Event handler
   */
  private lineChartCustomizedTimeInterval = (): void => {
    const config = this.model?.getConfig();
    if (config?.id !== undefined) {
      this.isTimeIntervalCustomized.set(config.id, true);
    }
  };

  /**
   * @category Event handler
   */
  private lineChartResetTimeInterval = (): void => {
    const config = this.model?.getConfig();
    if (config?.id !== undefined) {
      this.isTimeIntervalCustomized.set(config.id, false);
    }
  };

  /**
   * @param e The event carrying the new pie chart mode
   * @category Event handler
   */
  private pieModeChange = async (e: CustomEvent): Promise<void> => {
    this.isCreate = true;
    this.isCreateChange(this.isCreate);

    await this.createModel({
      chartType: WebChartTypes.PieSeries,
      config: undefined,
      overwriteId: this.selectedChartId,
      pieMode: e.detail,
    });

    this.createChartElement();
  };

  /**
   * @param chartData The chart's data
   * @category private
   */
  private getDataExtent = (chartData?: WebChartDataItem[]): CalculatedMinMaxBoundsPayload[] => {
    const config = this.model?.getConfig();
    let dataExtent: CalculatedMinMaxBoundsPayload[] = [];
    if (config !== undefined && chartData !== undefined) {
      const dataMinMax = getDataMinMaxFromSeries({
        chartConfig: config,
        chartData,
      });
      const { minY, maxY, minX, maxX } = dataMinMax;

      dataExtent = [
        { isXAxis: true, minimum: minX, maximum: maxX },
        { isXAxis: false, minimum: minY, maximum: maxY },
      ];
    }
    return dataExtent;
  };

  /**
   * Assigns some specific data to the config component once the data is processed by the chart
   * @param e The event carrying the data processed for the chart
   * @category Event handler
   */
  private dataProcessComplete = (e: CustomEvent): void => {
    const currentChartType = this.model?.getChartType();
    if (this.configElement !== undefined) {
      switch (currentChartType) {
        case WebChartTypes.HistogramSeries:
          this.configElement.chartData = e.detail;
          this.chartData = e.detail;
          break;
        case WebChartTypes.ScatterSeries: {
          const scatterPlotData = e.detail as WebChartScatterPlotDataItem;
          const { dataItems, r2 } = scatterPlotData;
          let newR2: number | undefined = r2;
          // Necessary check because a NaN value for instance will trigger an infinite rendering loop (see issues #2502 & #4079)
          // TODO: Find the reason of the infinite loop would help avoiding this check
          if (!Number.isFinite(r2)) {
            newR2 = undefined;
          }
          this.configElement.r2 = newR2;
          this.r2 = newR2;
          this.configElement.calculatedDataMinMaxBounds = this.getDataExtent(dataItems);
          break;
        }
        case WebChartTypes.LineSeries: {
          const { dataItems, timeIntervalInfo } = e.detail as WebChartGenericDataItem;
          if (isLineChartModel(this.model)) {
            if (
              timeIntervalInfo?.timeIntervalSize !== undefined &&
              this.model.getTimeIntervalSize() !== timeIntervalInfo?.timeIntervalSize
            ) {
              this.model.setTimeIntervalSize(timeIntervalInfo?.timeIntervalSize);
            }
            if (
              timeIntervalInfo?.timeIntervalUnits !== undefined &&
              this.model.getTimeIntervalUnits() !== timeIntervalInfo?.timeIntervalUnits
            ) {
              this.model.setTimeIntervalUnits(timeIntervalInfo?.timeIntervalUnits);
            }
          }

          this.disableFBEForLineChart();

          this.configElement.calculatedDataMinMaxBounds = this.getDataExtent(dataItems);
          break;
        }
        default:
          break;
      }
    }
  };

  /**
   * Updates config props on app and configElement to sync changes from chartElement
   * @category Event handler
   */
  private noRenderPropChange = (
    e: CustomEvent<{
      propName: string;
      value: WebChartLegendPositions | boolean;
    }>,
  ): void => {
    if (this.model !== undefined) {
      const { propName, value } = e.detail;
      const model = this.model as StringIndexable<ChartModel>;
      model[propName] = value;
    }
  };

  /**
   * Assigns new min/max bounds to the config component in order to align ac-js min/max
   * with ac-config
   * @category Event handler
   */
  private axesMinMaxChange = (e: CustomEvent<CalculatedMinMaxBoundsPayload[]>): void => {
    const currentChartType = this.model?.getChartType();
    if (this.configElement !== undefined) {
      switch (currentChartType) {
        case WebChartTypes.BarSeries:
        case WebChartTypes.BoxPlotSeries:
        case WebChartTypes.LineSeries:
        case WebChartTypes.HistogramSeries:
        case WebChartTypes.ScatterSeries:
          this.configElement.calculatedMinMaxBounds = e.detail;
          break;
        default:
          break;
      }
    }
  };

  /**
   * Applies the visibility change to config.
   * Also resets the min/max bounds to null when legend item visibility changes to allow
   * new bounds to be set for the visible items
   * @category Event handler
   */
  private legendItemVisibilityChange = async (e: CustomEvent<LegendItemVisibility>): Promise<void> => {
    this.model?.setSeriesVisibility(e.detail);

    // Reset axes bounds
    if (isModelWithXYAxes(this.model)) {
      // Reset y-axis bounds
      if (this.model.getMinYBound() === null || Number.isNaN(this.model.getMinYBound())) {
        await this.model.setMinYBound(null);
      }
      if (this.model.getMaxYBound() === null || Number.isNaN(this.model.getMaxYBound())) {
        await this.model.setMaxYBound(null);
      }

      // Reset x-axis bounds for line chart
      if (isLineChartModel(this.model)) {
        if (this.model.getMinXBound() === null || Number.isNaN(this.model.getMinXBound())) {
          await this.model.setMinXBound(null);
        }
        if (this.model.getMaxXBound() === null || Number.isNaN(this.model.getMaxXBound())) {
          await this.model.setMaxXBound(null);
        }
      }
    }
  };

  /**
   * Assigns new series color to the config component in order to align ac-js colors
   * with ac-config
   * @category Event handler
   */
  private seriesColorChange = (
    e: CustomEvent<{
      chartId: string;
      data: PieSlicesSymbols | SeriesSymbolsMap;
    }>,
  ): void => {
    if (this.configElement !== undefined && e.detail.chartId === this.selectedChartId) {
      switch (this.model?.getChartType()) {
        case WebChartTypes.BarSeries:
        case WebChartTypes.BoxPlotSeries:
        case WebChartTypes.LineSeries:
        case WebChartTypes.HistogramSeries:
          // updating the config component
          this.configElement.seriesColor = e.detail.data as SeriesSymbolsMap;
          break;
        case WebChartTypes.PieSeries: {
          const data = e.detail.data as PieSlicesSymbols;
          const pieSeriesSymbolsMap = new Map<number, SeriesSymbols>([[0, data]]);

          // update the config component
          this.configElement.seriesColor = pieSeriesSymbolsMap;
          break;
        }
        case WebChartTypes.ScatterSeries:
        default:
          break;
      }
    }
  };

  /**
   * @category Event handler
   */
  private updateComplete = (e: CustomEvent<ValidationStatus>): void => {
    const payload = e.detail;

    if (this.chartElement?.placeholder === undefined) {
      if (payload.schemaErrors.length > 0) {
        this.chartElement?.notify(this.strings?.invalidConfig, undefined, {
          closable: false,
        });
      } else if (payload.warnings.length > 0) {
        const aggregationWarningPresent = payload.warnings.some(
          (warning: ConfigWarningObject) => warning.keyword === ConfigWarningKeywords.NonNumericAggregation,
        );
        if (aggregationWarningPresent) {
          this.chartElement?.notify(this.strings?.validateAggregationTypeMsg, this.strings?.missingNumericFields, {
            closable: false,
          });
        }
      }
    }

    // If bar or line chart, we need to check for the no-agg + no-numeric fields case, which indicates an incomplete configuration
    if (isBarChartModel(this.model) || isLineChartModel(this.model)) {
      // check if "No aggregation" is selected
      if (
        this.model.getAggregationType() === RESTStatisticType.NoAggregation &&
        this.model.getNumericFields().length === 1
      ) {
        const oidField = this.featureLayer.objectIdField;

        // check if the only numeric field is the OID field (no numeric fields selected)
        if (this.model.getNumericFields().includes(oidField)) {
          this.chartElement?.notify(
            this.strings?.noAggregationRequiresNumericFields,
            this.strings?.missingNumericFields,
            {
              closable: false,
            },
          );
        }
      }
    }
  };

  /**
   * Event handler for action bar action select event
   * @category Event handler
   */
  private onDefaultActionSelect = (e: CustomEvent<DefaultActionSelectPayload>): void => {
    if (this.chartElement) {
      const { actionId, actionActive } = e.detail;
      const actionComponent = e.target as HTMLArcgisChartsActionBarElement;

      const chartComponent = this.chartElement;

      switch (actionId) {
        case DefaultChartActions.RotateChart: {
          if (this.model !== undefined && isModelWithRotation(this.model)) {
            this.model.setRotatedState(actionActive);
          }
          break;
        }
        case DefaultChartActions.Legend: {
          chartComponent.legendVisibility = actionActive;
          if (isModelWithLegend(this.model)) {
            this.model.setLegendVisibility(actionActive);
          }
          break;
        }
        case DefaultChartActions.FilterBySelection: {
          // we make sure the chart's component selection is up to date before enabling the filter by selection
          if (
            this.chartElement !== undefined &&
            hasSelectionChanged(this.selectionData, this.chartElement.selectionData)
          ) {
            this.chartElement.selectionData = {
              ...this.selectionData,
              selectionSource: SelectionSource.ProgrammaticSelection,
            };
          }

          // Toggling the filter by selection on the chart's component
          chartComponent.filterBySelection = actionActive;

          // Toggling the filter by selection on the config component
          if (this.configElement !== undefined) {
            this.configElement.filterBySelection = actionActive;
          }

          // For line charts, reset the time interval unless it has been customized
          if (isLineChartModel(this.model)) {
            const chartId = this.model.getConfig().id;

            if (chartId !== undefined && this.isTimeIntervalCustomized.get(chartId) !== true) {
              this.model.resetTimeInterval();
            }
          }

          // Cancelling the filter by extent by removing the view from the chart's component
          if (actionActive) {
            chartComponent.view = undefined;
          }

          // Removing the active state from the filter by extent button
          actionComponent.filterByExtentToggle = ActionToggle.Inactive;

          // Disabling some actions
          const enabledState = actionActive ? ActionState.Disabled : ActionState.Enabled;
          actionComponent.selectionState = enabledState;
          actionComponent.zoomState = enabledState;
          actionComponent.fullExtentState = enabledState;
          actionComponent.switchSelectionState = enabledState;
          actionComponent.filterByExtentState = enabledState;

          // if the action is toggled off while there's no selection left, or if the chart is a pie from fields, we disable it
          actionComponent.filterBySelectionState =
            getChartSubType(this.model?.getConfig().series ?? []) === ChartSubType.PieFromFields ||
            (!actionActive && this.isSelectionEmpty())
              ? ActionState.Disabled
              : ActionState.Enabled;
          break;
        }
        case DefaultChartActions.FilterByExtent: {
          if (this.view !== undefined) {
            // Setting the view on the chart's component
            chartComponent.view = actionActive ? this.view : undefined;

            // Toggling off the filter by selection on the chart's component
            if (chartComponent.filterBySelection === true) {
              chartComponent.filterBySelection = false;
            }

            // Toggling off the filter by selection on the config component
            if (this.configElement !== undefined && this.configElement.filterBySelection === true) {
              this.configElement.filterBySelection = false;
            }

            // Removing the active state from the filter by selection button
            actionComponent.filterBySelectionToggle = ActionToggle.Inactive;

            // disable FBS when there is no selection or FBE is enabled or when the chart is a pie from fields
            actionComponent.filterBySelectionState =
              getChartSubType(this.model?.getConfig().series ?? []) === ChartSubType.PieFromFields ||
              this.isSelectionEmpty() ||
              actionActive
                ? ActionState.Disabled
                : ActionState.Enabled;

            // Re-enabling some actions that might have been disabled when the filter by selection was toggled on
            actionComponent.selectionState = ActionState.Enabled;
            actionComponent.zoomState = ActionState.Enabled;
            actionComponent.fullExtentState = ActionState.Enabled;
            actionComponent.switchSelectionState = ActionState.Enabled;
          }
          break;
        }
        case DefaultChartActions.ClearSelection:
        case DefaultChartActions.Selection:
        case DefaultChartActions.Zoom:
        case DefaultChartActions.SwitchSelection:
        case DefaultChartActions.FullExtent:
        default:
          // Those actions are natively handled by the chart component. see onActionBarActionSelect()
          break;
      }
    }
  };

  /**
   * Event handler for action bar toggle
   * @category Event handler
   */
  private onActionBarToggle = (e: CustomEvent<boolean>): void => {
    this.actionBarExpanded = e.detail;
  };

  /**
   * Event handler for open chart authoring action
   * @category Event handler
   */
  private onOpenChartConfigClick = (): void => {
    // Changing config pane state
    if (!this.openConfig) {
      this.openConfig = true;
      this.panelNumber = 1;
      this.arcgisChartsAppChartsConfigOpen.emit();
    } else if (this.panelNumber !== 0) {
      this.openConfig = false;
      this.arcgisChartsAppChartsConfigClose.emit();
    } else {
      // Changing panel number
      this.panelNumber = 1;
    }

    this.createOrUpdateChartAuthoringAction();
  };

  /**
   * Event handler for view time zone change
   * @category Event handler
   */
  private viewTimeZoneChange = (): void => {
    if (this.view !== undefined && "timeZone" in this.view) {
      // set the time zone on the chart component
      if (this.chartElement !== undefined && (isBarChartModel(this.model) || isLineChartModel(this.model))) {
        (this.chartElement as HTMLArcgisChartsBarChartElement | HTMLArcgisChartsLineChartElement).timeZone =
          this.view?.timeZone;
      }

      // set the time zone on the config component
      if (this.configElement !== undefined) {
        this.configElement.timeZone = this.view?.timeZone;
      }
    }
  };

  /**
   * @category Private
   */
  private closeChart(): void {
    this.chartPanel?.remove();
    this.chartPanel = undefined;
    this.actionBarElement?.remove();
    this.actionBarElement = undefined;
    this.selectedChartId = undefined;
    this.model = undefined;
    if (this.chartReferenceElement !== undefined) {
      this.chartReferenceElement.hidden = true;
    }
    if (this.chartElement !== undefined) {
      this.chartElement.config = undefined;
    }
    if (this.configElement !== undefined) {
      this.panelNumber = 0;
    }
    this.clearSelection();
    (this.tabManagerElement as any)?.removeTab(this.chartTabId);
  }

  /**
   *
   * @category Private
   */
  private clearSelection(): void {
    if (this.chartElement !== undefined) {
      this.chartElement.clearSelection();
    }
  }

  /**
   * @category Private
   */
  private async getCurrentChartTypeText(): Promise<string> {
    if (this.strings === undefined) {
      ({ strings: this.strings } = await fetchComponentLocaleStrings(this.hostElement, getAssetPath(`.`)));
    }
    const currentChartType = this.model?.getChartType();
    let chartTypeText: string;
    switch (currentChartType) {
      case WebChartTypes.BarSeries:
        chartTypeText = this.strings?.barChart;
        break;
      case WebChartTypes.BoxPlotSeries:
        chartTypeText = this.strings?.boxPlot;
        break;
      case WebChartTypes.LineSeries:
        chartTypeText = this.strings?.lineChart;
        break;
      case WebChartTypes.PieSeries:
        chartTypeText = this.strings?.pieChart;
        break;
      case WebChartTypes.HistogramSeries:
        chartTypeText = this.strings?.histogram;
        break;
      case WebChartTypes.ScatterSeries:
        chartTypeText = this.strings?.scatterPlot;
        break;
      default:
        chartTypeText = this.strings?.chart;
        break;
    }
    return chartTypeText;
  }

  /**
   * Handle selection change.
   * @category Private
   */
  private handleSelectionChange = async (
    newSelectionData: SelectionData,
    featureLayer: SupportedLayer,
  ): Promise<void> => {
    const mainLayer = getMainLayer(featureLayer);
    // Do the calculation only when the view is defined.
    // if app is used by other consumers which needs selected items add event later.
    if (this.view !== undefined && !mainLayer?.isTable) {
      // updating the @Prop and the map's selection only if there's a change
      const highlightInitializationNeeded = !this.isSelectionEmpty() && this.highlightSelect === undefined;
      if (hasSelectionChanged(newSelectionData, this.selectionData) || highlightInitializationNeeded) {
        this.selectionData = { ...newSelectionData };

        const featureLayerView = await this.view.whenLayerView(mainLayer);
        // We need to call remove() before highlight(), otherwise it cumulates the selected features
        this.highlightSelect?.remove();

        // TODO: WHEN REMOVING THIS, PLEASE ALSO REMOVE THE SAME FROM `charts-shared-utils\src\feature-layer\index.ts`. This typeguard is needed temporarily since there is an issue in @arcgis/core where `view.whenLayerView(layer)` will return a `LayerView` type when given a `SubtypeGroupLayer` argument even though it is in fact an instance of `FeatureLayerView`. Once this has been fixed we can remove the following `hasHightlight typeguard and following `if` statament.
        const hasHighlight = (layerView: LayerView): layerView is FeatureLayerView => "highlight" in layerView;
        if (hasHighlight(featureLayerView)) {
          this.highlightSelect = featureLayerView.highlight(this.selectionData?.selectionOIDs);
        }
      }
    }

    this.setClearSelectionActionState();
  };

  /**
   * Update popover open to chart elements when there is a change.
   * @param e
   * @category Private
   */
  private changePopoverOpenChange = (e: CustomEvent): void => {
    if (this.chartElement !== undefined && "disableInteractions" in this.chartElement) {
      (this.chartElement as HTMLArcgisChartsZoomableChartElement).disableInteractions = e.detail.open;
    }
  };

  /**
   * disable FBE for temporal line Chart:
   * - when some chart elements are selected and FBS is not activated
   * - when no elements are selected (default case) or FBS is not activated
   * @Category Private
   */
  private disableFBEForLineChart(): void {
    if (
      this.actionBarElement !== undefined &&
      (this.isSelectionEmpty() || this.actionBarElement.filterBySelectionToggle === ActionToggle.Inactive)
    ) {
      // TODO: re-evaluate if this was the correct logic (enabling FBE here) or if this was a mistake
      this.actionBarElement.filterByExtentState = ActionState.Enabled;
    }
  }

  /**
   * Adds a listener on the view, so every time the time zone is modified we update
   * the chart.
   * @category Private
   */
  private async addViewTimeZoneListener(): Promise<IHandle | undefined> {
    let newListener: IHandle | undefined;

    // checking whether the "timeZone" property exists on the view as it's currently available only on the MapView and not the SceneView
    // TODO: revisit this condition and casting for 2024 R01 when SceneView supports the timeZone
    if (this.view !== undefined && "timeZone" in this.view) {
      const reactiveUtils = await importCoreReactiveUtils();

      // add a watch handler on the "timeZone" property of the view
      newListener = reactiveUtils.watch(() => (this.view as MapView).timeZone, this.viewTimeZoneChange);
    }

    return newListener;
  }
}
